import { fetchDepositOptions } from '@/services/deposit-options'
import { useUserStore } from '@/stores/user'
import type { DepositBonusOption } from '@/types'
import {
  MAX_BUNDLE_COINS,
  MIN_BUNDLE_COINS,
  extractBonusPercent,
  getDisplayAmount,
  isDepositPromotionActiveForRouting,
} from '@/utils/deposit-utils'
import { ProductSku, type TProductSku } from '@/utils/helper-objects'
import { type Ref, computed, ref } from 'vue'

type LoadResult = { success: boolean; error: any }

const depositAmounts = ref(new Map<TProductSku, DepositBonusOption>()) as Ref<
  Map<TProductSku, DepositBonusOption>
>
const firstDepositXpEligible = ref(false)
const isApiUnavailable = ref(false)

let hasLoadedSuccessfully = false
let lastLoadResult: LoadResult = { success: false, error: null }
let inflightLoad: Promise<LoadResult> | null = null

function isEligiblePromotion(
  option: DepositBonusOption | null | undefined
): option is DepositBonusOption {
  return (
    !!option &&
    isDepositPromotionActiveForRouting(option) &&
    option.min_deposit_minor !== undefined &&
    option.max_deposit_minor !== undefined
  )
}

function findFirstEligiblePromotion(
  map: Map<TProductSku, DepositBonusOption>
): DepositBonusOption | null {
  for (const option of map.values()) {
    if (isEligiblePromotion(option)) {
      return option
    }
  }
  return null
}

function findWelcomePromotion(
  map: Map<TProductSku, DepositBonusOption>
): DepositBonusOption | null {
  const option = map.get(ProductSku.DepositWelcomeCustom100) ?? null
  return isEligiblePromotion(option) ? option : null
}

async function fetchAndPopulate(): Promise<LoadResult> {
  try {
    const { data, error } = await fetchDepositOptions(depositAmounts.value)

    if (error.value || !data.value || depositAmounts.value.size === 0) {
      console.error(
        '[useDepositOptions] Failed to fetch deposit options:',
        error.value
      )
      isApiUnavailable.value = true
      hasLoadedSuccessfully = false
      lastLoadResult = { success: false, error: error.value }
      return lastLoadResult
    }

    // Reassign to trigger reactivity on Map mutations
    depositAmounts.value = new Map(depositAmounts.value)
    firstDepositXpEligible.value = data.value.first_deposit_xp_eligible

    isApiUnavailable.value = false
    hasLoadedSuccessfully = true
    lastLoadResult = { success: true, error: null }
    return lastLoadResult
  } catch (e) {
    console.error('[useDepositOptions] Exception fetching deposit options:', e)
    isApiUnavailable.value = true
    firstDepositXpEligible.value = false
    hasLoadedSuccessfully = false
    lastLoadResult = { success: false, error: e }
    return lastLoadResult
  }
}

export function useDepositOptions() {
  const promotionDepositOption = computed<DepositBonusOption | null>(() =>
    findFirstEligiblePromotion(depositAmounts.value)
  )

  const welcomeDepositOption = computed<DepositBonusOption | null>(() => {
    return findWelcomePromotion(depositAmounts.value)
  })

  /**
   * Get the custom wallet option dynamically from API response.
   */
  const customWalletOption = computed<DepositBonusOption | null>(() => {
    return (
      Array.from(depositAmounts.value.values()).find(
        (opt) => opt.product_type === 'wallet_top_up'
      ) ?? null
    )
  })

  /**
   * Get the first available catalog bundle option (for fallbacks).
   */
  const firstCatalogBundle = computed<DepositBonusOption | null>(() => {
    return (
      Array.from(depositAmounts.value.values()).find(
        (opt) => opt.type === 'catalog' && opt.product_type === 'bundle'
      ) ?? null
    )
  })

  /**
   * Get the highest cost catalog bundle (for default selection when no promotion).
   */
  const highestCostCatalogBundle = computed<DepositBonusOption | null>(() => {
    const bundles = Array.from(depositAmounts.value.values()).filter(
      (opt) => opt.type === 'catalog' && opt.product_type === 'bundle'
    )
    if (bundles.length === 0) {
      return null
    }
    return bundles.sort(
      (a, b) => (b.price_minor ?? 0) - (a.price_minor ?? 0)
    )[0]
  })

  /**
   * Get max deposit amount from wallet metadata.
   */
  const maxDepositAmount = computed<number>(() => {
    const wallet = customWalletOption.value
    if (wallet?.metadata?.max_credits) {
      return wallet.metadata.max_credits
    }
    // Find highest max_deposit_minor from promotions
    const maxPromo = Array.from(depositAmounts.value.values())
      .filter((opt) => opt.type === 'promotion' && opt.max_deposit_minor)
      .map((opt) => opt.max_deposit_minor!)
      .sort((a, b) => b - a)[0]
    return maxPromo ?? 0
  })

  /**
   * Get min deposit amount from wallet metadata or promotion min_deposit_minor.
   */
  const minDepositAmount = computed<number>(() => {
    const wallet = customWalletOption.value
    if (wallet?.metadata?.min_credits) {
      return wallet.metadata.min_credits
    }
    // Check promotions for min_deposit_minor
    const minPromo = Array.from(depositAmounts.value.values())
      .filter((opt) => opt.type === 'promotion' && opt.min_deposit_minor)
      .map((opt) => opt.min_deposit_minor!)
      .sort((a, b) => a - b)[0]
    return minPromo ?? 0
  })

  /**
   * Convert minium deposit amount to pounds for easier display .
   */
  const minDepositPounds = computed<number>(() =>
    Math.ceil(minDepositAmount.value / 100)
  )

  /**
   * Convert maximum deposit amount to pounds for easier display.
   */
  const maxDepositPounds = computed<number>(() =>
    Math.floor(maxDepositAmount.value / 100)
  )

  /**
   * Filter visible deposit options, excluding revoked/fulfilled promotions and wallet top-up.
   */
  const visibleDepositOptions = computed<DepositBonusOption[]>(() => {
    return Array.from(depositAmounts.value.values()).filter((option) => {
      if (option.product_type === 'wallet_top_up') {
        return false
      }
      if (option.type === 'catalog') {
        return true
      }
      return option.status !== 'revoked' && option.status !== 'fulfilled'
    })
  })

  /**
   * Check if we have any deposit options at all.
   */
  const hasAnyDepositOptions = computed<boolean>(() => {
    if (isApiUnavailable.value) {
      return false
    }
    return (
      visibleDepositOptions.value.length > 0 ||
      customWalletOption.value !== null
    )
  })

  /**
   * Pre-calculate coin count, display amount, and bonus for each visible option.
   */
  const depositOptionsWithCoinCount = computed<
    Array<
      DepositBonusOption & {
        coinCount: number
        silverCoinCount: number
        displayAmount: number
        bonusPercent: number
      }
    >
  >(() => {
    const optionsWithTotals = visibleDepositOptions.value.map((option) => {
      const displayAmount = getDisplayAmount(option)
      const bonusPercent = extractBonusPercent(option)

      return {
        ...option,
        displayAmount,
        bonusPercent,
        coinCount: 1,
        silverCoinCount: 0,
      }
    })

    // Find max display amount for proportional scaling
    const allAmounts = optionsWithTotals.map((opt) => opt.displayAmount)
    const maxAmount = Math.max(...allAmounts)

    // Scale coins proportionally based on price ratio using defined constants
    return optionsWithTotals.map((option) => {
      // Proportional scaling: price ratio × max bundle coins
      const totalCoins = Math.max(
        MIN_BUNDLE_COINS,
        Math.round((option.displayAmount / maxAmount) * MAX_BUNDLE_COINS)
      )

      // Calculate silver coins for bonus (rounded up), but always keep at least 1 gold coin
      const silverCoins = Math.min(
        totalCoins - 1,
        Math.ceil(totalCoins * option.bonusPercent)
      )

      return {
        ...option,
        coinCount: totalCoins,
        silverCoinCount: silverCoins,
      }
    })
  })

  /**
   * Load deposit options from API and populate the reactive Map.
   * Returns success status and any error encountered.
   *
   * @returns {Promise<{success: boolean, error: any}>} Result of the load operation
   */
  async function loadDepositOptions(options?: { force?: boolean }) {
    const force = options?.force ?? false

    if (
      (!force &&
        hasLoadedSuccessfully &&
        depositAmounts.value.size > 0 &&
        !inflightLoad) ||
      useUserStore().isLoggedOut
    ) {
      return lastLoadResult
    }

    if (!inflightLoad) {
      inflightLoad = (async () => {
        const result = await fetchAndPopulate()
        inflightLoad = null
        return result
      })()
    }

    return inflightLoad
  }

  return {
    customWalletOption,
    depositAmounts,
    depositOptionsWithCoinCount,
    firstCatalogBundle,
    firstDepositXpEligible,
    hasAnyDepositOptions,
    highestCostCatalogBundle,
    isApiUnavailable,
    loadDepositOptions,
    maxDepositAmount,
    maxDepositPounds,
    minDepositAmount,
    minDepositPounds,
    promotionDepositOption,
    welcomeBonus: welcomeDepositOption,
    visibleDepositOptions,
  }
}
